/**
 * Typed API client.
 *
 * The backend answers every error with RFC 9457 Problem Details, so this parses that shape
 * rather than guessing at error bodies. That matters most for `422` on the telemetry range
 * endpoint: it returns a *remedy* ("request at most 5 series for this range"), and dropping
 * it in favour of a generic "Request failed" would throw away the one piece of information
 * that lets the user fix their own query.
 */
import type { Session } from '@supabase/supabase-js';

const BASE = (import.meta.env.VITE_API_URL ?? 'http://localhost:4000').replace(/\/$/, '');

export interface ProblemDetails {
  type: string;
  title: string;
  status: number;
  detail: string;
  instance?: string;
  requestId?: string;
  errors?: Array<{ path: string; message: string }>;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly problem: ProblemDetails,
    /** Correlates with the backend log line for this request. */
    readonly requestId?: string,
    /**
     * From a `Retry-After` header, in ms. Only ever set on a 429 — `lib/api/backoff.ts`
     * reads this to pause polling for the window the server actually asked for, instead of
     * guessing a backoff that may be shorter or longer than what actually clears it.
     */
    readonly retryAfterMs: number | null = null,
  ) {
    super(problem.detail || problem.title || `HTTP ${status}`);
    this.name = 'ApiError';
  }

  /** Field errors keyed by path, for form rendering. */
  get fieldErrors(): Record<string, string> {
    const out: Record<string, string> = {};
    for (const e of this.problem.errors ?? []) out[e.path] = e.message;
    return out;
  }

  /**
   * A 422 from the range endpoint carries the cheaper query to run instead. Surfacing it
   * verbatim turns a dead end into a fixable mistake.
   */
  get remedy(): string | null {
    const m = /Try: (.+)$/.exec(this.problem.detail ?? '');
    return m?.[1] ?? null;
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  /** Retrying these is pointless — the request itself is wrong. */
  get isClientFault(): boolean {
    return this.status >= 400 && this.status < 500 && this.status !== 429;
  }
}

/**
 * Session provider, injected rather than imported.
 *
 * Keeps this module free of a Supabase dependency, so it is trivially testable and does not
 * drag an auth client into every unit test that touches the API surface.
 */
type SessionGetter = () => Session | null;
let getSession: SessionGetter = () => null;

export function setSessionProvider(fn: SessionGetter): void {
  getSession = fn;
}

/** Called on a 401 so the app can route to login exactly once. */
type UnauthorizedHandler = () => void;
let onUnauthorized: UnauthorizedHandler = () => {};

export function setUnauthorizedHandler(fn: UnauthorizedHandler): void {
  onUnauthorized = fn;
}

/**
 * ─────────────────────────────────────────────────────────────────────────────
 * WHEN THIS CALLER'S AUTHORITY CHANGES UNDER THEM
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * The server re-reads role, permissions and reach on EVERY request, so a change bites on the
 * next call. The console had no way to learn that: `/me` is cached for five minutes, so a
 * demoted operator kept the old navigation and the old lists long after the server stopped
 * honouring them. They could not act — every write was refused — but a screen offering doors
 * that are already locked reads as a broken product rather than as a permission they lack,
 * which is the rule `CLAUDE.md` states for this repo.
 *
 * TWO SIGNALS FEED THIS, and they cover different halves:
 *
 *   · `x-lumen-access` on every response — a digest of the permissions, reach and scope the
 *     server just enforced with. It rides on the caller's own traffic, so it corrects them at
 *     the exact moment they act, and it cannot disagree with the enforcement because it is
 *     computed from the same values.
 *   · a 403 — a refusal IS a statement about authority, and the one signal that arrives even
 *     if the digest were somehow missing.
 *
 * Deliberately NOT polling and NOT a push channel. Polling asks thousands of times about an
 * event that happens a few times a year; a second channel is a second thing that can disagree.
 * An idle caller is never corrected, and does not need to be — they are not acting on it.
 */
type AccessChangeHandler = () => void;
let onAccessChanged: AccessChangeHandler = () => {};
export function setAccessChangedHandler(fn: AccessChangeHandler): void {
  onAccessChanged = fn;
}

/** The last digest seen. `null` until the first response, which establishes the baseline. */
let lastAccessDigest: string | null = null;
let accessNotifyAt = 0;

/**
 * Announce a change, at most once a second.
 *
 * THE DEBOUNCE IS LOAD-BEARING, not tidiness. A screen mounts eight queries at once; if the
 * digest moved, all eight responses carry the new value and all eight would fire an
 * invalidation, each triggering refetches that carry the same digest again. One page load
 * becomes a storm. The first response through the door is the one that matters.
 */
function announceAccessChange(): void {
  const now = Date.now();
  if (now - accessNotifyAt < 1_000) return;
  accessNotifyAt = now;
  onAccessChanged();
}

/** Called for every response, success or failure. */
function noteAccessDigest(res: Response): void {
  const digest = res.headers.get('x-lumen-access');
  if (digest === null) return;
  if (lastAccessDigest === null) {
    lastAccessDigest = digest;
    return;
  }
  if (digest === lastAccessDigest) return;
  lastAccessDigest = digest;
  announceAccessChange();
}

/** Forgets the baseline, so the next response establishes a new one. For sign-out/sign-in. */
export function resetAccessDigest(): void {
  lastAccessDigest = null;
}

/**
 * Refresh the session, returning true if a new access token is now available.
 *
 * Injected for the same reason as the session getter: this module stays free of a Supabase
 * dependency, so it is testable without an auth client.
 */
type SessionRefresher = () => Promise<boolean>;
let refreshSession: SessionRefresher = async () => false;

export function setSessionRefresher(fn: SessionRefresher): void {
  refreshSession = fn;
}

/**
 * SINGLE-FLIGHT REFRESH, and this is the load-bearing part.
 *
 * When a tab wakes, every mounted query fires at once and all of them get 401 together. If
 * each one refreshed independently, GoTrue's refresh-token ROTATION would see a burst of
 * concurrent redemptions of the same token, revoke the losers, and log the user out — the
 * exact failure this code exists to prevent, made worse by trying to fix it.
 *
 * So the first 401 starts the refresh and every other caller awaits the same promise.
 */
let inFlight: Promise<boolean> | null = null;

function refreshOnce(): Promise<boolean> {
  inFlight ??= refreshSession().finally(() => {
    inFlight = null;
  });
  return inFlight;
}

/**
 * How long a single API request may hang before it is abandoned.
 *
 * See the note at the `fetch` call: `fetch` has no default, and on a stalled connection
 * Kong's 60s never applies because the request never reaches Kong.
 */
const REQUEST_TIMEOUT_MS = 30_000;

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  /** Serialised to a query string; `undefined` and `null` entries are dropped. */
  query?: Record<string, string | number | boolean | undefined | null>;
  body?: unknown;
  signal?: AbortSignal;
  headers?: Record<string, string>;
  /**
   * Answer THIS request in another organisation the caller administers, leaving the header's
   * selection alone — see `api.within`. Absent means the selection, as for every other request.
   */
  organisation?: string;
}

/** `Retry-After` is seconds per RFC 9110 §10.2.3; `@fastify/rate-limit` always sends the numeric form. */
function parseRetryAfterMs(header: string | null): number | null {
  if (header === null) return null;
  const seconds = Number(header);
  return Number.isFinite(seconds) && seconds >= 0 ? seconds * 1000 : null;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = new URL(`${BASE}/api/v1${path.startsWith('/') ? path : `/${path}`}`);
  for (const [k, v] of Object.entries(query ?? {})) {
    if (v === undefined || v === null || v === '') continue;
    url.searchParams.set(k, String(v));
  }
  return url.toString();
}

/**
 * One HTTP round trip. No retry, no refresh — `request` owns that policy.
 */
/**
 * The organisation every request is scoped to, or null for the caller's own.
 *
 * A module variable because this file is not a React component and cannot read context, and
 * because the header belongs in the ONE place every request already passes through rather than
 * at two hundred call sites. `app/org-context.tsx` is the only writer.
 *
 * The server validates it: an id outside the caller's reach is ignored and they fall back to
 * their own organisation. So a stale value here cannot widen anything — it can only fail to
 * narrow, which is the safe direction.
 */
let requestOrganisation: string | null = null;

export function setRequestOrganisation(id: string | null): void {
  requestOrganisation = id;
}

async function attempt(path: string, opts: RequestOptions): Promise<Response> {
  const session = getSession();
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...opts.headers,
  };
  const organisation = opts.organisation ?? requestOrganisation;
  if (organisation) {
    headers['X-Lumen-Organisation'] = organisation;
  }
  if (session?.access_token) {
    headers.Authorization = `Bearer ${session.access_token}`;
  }
  if (opts.body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }

  /*
   * ─────────────────────────────────────────────────────────────────────────────
   * A CEILING ON HOW LONG A REQUEST MAY HANG.
   * ─────────────────────────────────────────────────────────────────────────────
   *
   * There was none. `fetch` has no default timeout, so the only ceiling was Kong's 60s —
   * and on a connection that stalls rather than fails (a train, a lift, a captive portal)
   * even that never arrives, because the request never reaches Kong. The spinner runs
   * forever and the operator has no way to tell "slow" from "broken".
   *
   * 30 SECONDS, and it is deliberately not tighter. Every ordinary call here answers in
   * well under a second; the ones that legitimately take longer are report generation and
   * the first load of a large twin, and those are still comfortably inside it. A timeout
   * that fires on a slow-but-working request is worse than none, because it turns a wait
   * into an error the reader must interpret.
   *
   * The caller's own signal still wins where one is given — a query that unmounts should
   * abort immediately, not in thirty seconds — so the two are COMBINED rather than one
   * replacing the other.
   */
  const timeout = AbortSignal.timeout(REQUEST_TIMEOUT_MS);
  const signal =
    opts.signal === undefined
      ? timeout
      : /* `AbortSignal.any` is the standard combinator; where it is missing the caller's
           signal is the more important of the two, since it prevents work rather than
           bounding it. */
        typeof AbortSignal.any === 'function'
        ? AbortSignal.any([opts.signal, timeout])
        : opts.signal;

  return fetch(buildUrl(path, opts.query), {
    method: opts.method ?? 'GET',
    headers,
    ...(opts.body !== undefined ? { body: JSON.stringify(opts.body) } : {}),
    signal,
  });
}

/**
 * A request, with ONE session refresh on 401.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THIS EXISTS: SESSIONS WERE DYING ON IDLE
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * The access token lives an hour and `autoRefreshToken` renews it about a minute before
 * expiry — while the tab is awake. Browsers throttle timers in a backgrounded tab, so a tab
 * left alone for over an hour does not get its scheduled refresh. The user returns, the first
 * request goes out with a stale token, and the API answers 401.
 *
 * This function used to hand that 401 straight to `onUnauthorized()` and route to the login
 * screen — with a perfectly valid refresh token still sitting in storage, untried. That is
 * what "the session expires very quickly" was: not an expiry policy, a missing retry.
 *
 * So a 401 now buys ONE refresh and ONE replay. Only if the refresh itself fails, or the
 * replay is also refused, is the user actually signed out.
 *
 * Fixing it here rather than by raising `GOTRUE_JWT_EXP` is deliberate. A longer access token
 * would make the symptom rarer while making every leaked token useful for longer, and an
 * access token cannot be revoked before it expires. A short access token with a long refresh
 * token is the design; it only needed the refresh to actually be attempted.
 */
/**
 * The checked response: refreshed once on a 401, the access digest read, an error thrown as
 * `ApiError`. `request` reads it as JSON; `download` as a file.
 */
async function respond(path: string, opts: RequestOptions): Promise<Response> {
  let res = await attempt(path, opts);

  if (res.status === 401 && getSession() !== null) {
    /*
     * Guarded on there BEING a session: an anonymous 401 is a genuine "you are not logged
     * in", and refreshing nothing to retry a request that will fail again just doubles the
     * latency before the login screen appears.
     */
    if (await refreshOnce()) {
      res = await attempt(path, opts);
    }
  }

  const requestId = res.headers.get('x-request-id') ?? undefined;
  /* The digest carries the organisation the server answered in, so one asked in ANOTHER
     organisation differs from the console's by construction. Reading it would announce an access
     change that did not happen, and the next ordinary response would announce a second. */
  if (opts.organisation === undefined || opts.organisation === requestOrganisation) {
    noteAccessDigest(res);
  }

  /*
   * A REFUSAL IS A FACT ABOUT AUTHORITY, so re-read who we are — except when the refusal came
   * from the very request that answers that question. `/me` 403-ing and triggering a refetch of
   * `/me` is the one recursion available here, and it would be a tight loop.
   */
  if (res.status === 403 && !path.startsWith('/me')) announceAccessChange();

  if (!res.ok) {
    let problem: ProblemDetails = {
      type: 'about:blank',
      title: res.statusText || 'Request failed',
      status: res.status,
      detail: '',
    };
    try {
      const parsed = (await res.json()) as Partial<ProblemDetails>;
      // A proxy or gateway can return HTML/text on an error, so only merge when the body
      // actually looks like Problem Details.
      if (parsed && typeof parsed === 'object' && 'status' in parsed) {
        problem = { ...problem, ...parsed } as ProblemDetails;
      }
    } catch {
      /* non-JSON error body; the default shape above stands */
    }

    const retryAfterMs = res.status === 429 ? parseRetryAfterMs(res.headers.get('retry-after')) : null;
    const err = new ApiError(res.status, problem, requestId, retryAfterMs);
    if (err.isUnauthorized) onUnauthorized();
    throw err;
  }

  return res;
}

export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const res = await respond(path, opts);
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/** The file name a `Content-Disposition: attachment; filename="…"` header names, if any. */
export function dispositionName(header: string | null): string | null {
  const m = header === null ? null : /filename="([^"]+)"/.exec(header);
  return m?.[1] ?? null;
}

/**
 * A file from the API, with the session's token — a plain link cannot carry it. For a report the
 * server generates (Lumen-BE `fleet-reports`), so the bytes are the server's and the numbers match.
 */
export async function download(path: string, query?: RequestOptions['query']): Promise<{ blob: Blob; name: string | null }> {
  const res = await respond(path, query ? { query } : {});
  return { blob: await res.blob(), name: dispositionName(res.headers.get('content-disposition')) };
}

export const api = {
  // `query` and `signal` are spread conditionally rather than passed as possibly-undefined:
  // under exactOptionalPropertyTypes an explicit `undefined` is not the same as an absent
  // key, and the former does not satisfy an optional property.
  get: <T>(path: string, query?: RequestOptions['query'], signal?: AbortSignal) =>
    request<T>(path, {
      ...(query ? { query } : {}),
      ...(signal ? { signal } : {}),
    }),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  // PUT, not PATCH, for attribute values: the body is the complete set for that entity,
  // so a field omitted from a form submission is genuinely absent rather than unchanged.
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  del: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  /**
   * The same calls, answered IN ANOTHER ORGANISATION the caller administers — for these requests
   * only, with the header's selection untouched.
   *
   * For a form that targets one organisation while the console works in another: inviting
   * somebody into a sub-organisation, and reading that organisation's roles and departments to
   * fill the form. The server validates the id exactly as it validates the selection, so an
   * organisation the caller does not administer is answered in their own, never wider.
   */
  within: (organisation: string) => ({
    get: <T>(path: string, query?: RequestOptions['query']) =>
      request<T>(path, { organisation, ...(query ? { query } : {}) }),
    post: <T>(path: string, body?: unknown) =>
      request<T>(path, { method: 'POST', body, organisation }),
  }),
};

export { BASE as API_BASE };
